<template>
    <v-dialog
        v-model="touched"
        max-width="520"
        persistent
    >
        <template #activator="{ on, attrs }">
            <slot v-bind="{ on, attrs }"></slot>
        </template>

        <v-card>
            <v-card-title class="customized-primary white--text">
                {{ t('app_structures_action_publish') }}
            </v-card-title>

            <v-card-text class="pb-0">
                <v-form class="mt-6">
                    <div class="form-group-horizontal">
                        <div class="horizontal-label">{{ t('app_structures_label_file') }}</div>
                        <div class="horizontal-input">
                            <v-file-input
                                v-model="form.file"
                                required
                                outlined
                                dense
                                prepend-icon=""
                                prepend-inner-icon="mdi-paperclip"
                                accept=".json,application/json"
                                :placeholder="t('file_manager_label_browse_files')"
                                :error-messages="form.errors.get('file')"
                                @change="form.errors.clear('file')"
                            ></v-file-input>
                        </div>
                    </div>

                    <div class="form-group-horizontal">
                        <div class="horizontal-label">{{ t('app_structures_label_platform') }}</div>
                        <div class="horizontal-input">
                            <v-select
                                v-model="form.platform"
                                :items="platforms"
                                outlined
                                dense
                                append-icon="mdi-chevron-down"
                                :error-messages="form.errors.get('platform')"
                                @change="form.errors.clear('platform')"
                            ></v-select>
                        </div>
                    </div>

                    <div class="form-group-horizontal">
                        <div class="horizontal-label">{{ t('app_structures_label_app_version') }}</div>
                        <div class="horizontal-input">
                            <v-text-field
                                v-model="form.app_version"
                                outlined
                                dense
                                placeholder="2.1.0"
                                :error-messages="form.errors.get('app_version')"
                            ></v-text-field>
                        </div>
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-btn
                    color="primary"
                    @click="submit"
                    width="45%"
                    :loading="form.busy"
                    :disabled="form.busy || !form.file"
                >
                    {{ t('app_structures_action_publish') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    color="orange"
                    @click="cancel"
                    width="45%"
                    dark
                    :disabled="form.busy"
                >
                    {{ t('general_action_cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import { useStore } from '@/store'
import { useForm } from '@/core/form'
import { useAppStructureHttp } from '@/core/api/app-structures'

/**
 * SSO-3534 — publishing the map of an app build from its JSON file.
 *
 * Deliberately has no hash field: the backend computes `sha256:` of the exact bytes uploaded,
 * which is the same rule the app uses for the file it bundles, so the two match by construction
 * and a mistyped hash is impossible.
 */
export default defineComponent({
    name: 'PublishAppStructureModal',

    setup(props, context) {
        const store = useStore()
        const i18n = useI18n()

        const state = reactive({
            touched: false,
        })

        const form = reactive(useForm({
            file: null,
            platform: 'ios',
            app_version: '',
        }))

        const platforms = [
            { text: 'iOS', value: 'ios' },
            { text: 'Android', value: 'android' },
        ]

        const submit = () => {
            const data = new FormData()
            data.append('file', <any>form.file)
            data.append('platform', form.platform)
            if (form.app_version) {
                data.append('app_version', form.app_version)
            }

            form
                .usingSubmit(useAppStructureHttp().upload<any>(data))
                .then((published: any) => {
                    state.touched = false
                    form.reset()

                    const key = published.replaced ? 'app_structures_notice_replaced' : 'app_structures_notice_published'
                    store.dispatch('notify', {
                        color: 'success',
                        message: i18n.t(key, { hash: published.version_hash }),
                    })

                    context.emit('success', published)
                })
        }

        const cancel = () => {
            form.reset()
            form.errors.clear()
            state.touched = false
            context.emit('cancel')
        }

        return {
            form,
            platforms,
            submit,
            cancel,
            ...useI18n(),
            ...toRefs(state)
        }
    }
})
</script>
